import React from 'react';
import {
  Flex,
  Card,
  CardContent,
  Heading,
  Text,
  Button,
  Divider,
  TextField,
  PasswordField,
  Spinner,
} from '@chatsdet/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { API_URL } from '../../config';
import { useEmailSignin, EmailAuthError } from '../../hooks/useEmailAuth';
import { useAuthConfig } from '../../hooks/useAuthConfig';
import { useInviteToken } from '../../hooks/useInviteToken';
import { getLoginReturnTo } from '../../lib/login-return-to';
import { BRAND } from '../../brand';
import { useAuth } from '../../contexts/AuthContext';
import { CofactorMark } from '../components/CofactorMark';

interface LoginSearch {
  invite?: string;
  error?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as LoginSearch;
  const inviteToken = useInviteToken(search.invite);
  const { name, tagline, termsUrl, privacyUrl } = BRAND;
  // Known before sign-in on a per-org host; null on the shared host, i.e.
  // everywhere today.
  const { hostOrganizationName } = useAuth();

  const [showEmailForm, setShowEmailForm] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(() => {
    if (search.error === 'ORGANIZATION_DISABLED') {
      return 'This organization is disabled. Please contact Support.';
    }
    return null;
  });

  React.useEffect(() => {
    if (search.error) {
      navigate({
        to: '/login',
        search: { invite: search.invite },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signinMutation = useEmailSignin();
  const { data: authConfig } = useAuthConfig();

  // Page the visitor was headed to (e.g. /cli-login); null means go home.
  const returnTo = getLoginReturnTo(window.location);
  // Invite is auto-accepted during signin; land back where the visitor was headed.
  const redirectUrl = returnTo ?? '/';

  const ssoUrl = (provider: 'google' | 'microsoft') => {
    const params = new URLSearchParams();
    if (inviteToken) params.set('invite', inviteToken);
    if (returnTo) params.set('returnTo', returnTo);
    const query = params.toString();
    return `${API_URL}/auth/${provider}${query ? `?${query}` : ''}`;
  };

  const handleEmailSignin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await signinMutation.mutateAsync({ email, password, invite: inviteToken });
      if (result.mfa) {
        navigate({ to: '/login/mfa', search: { mode: result.mfa, returnTo: returnTo ?? undefined } });
        return;
      }
      // On success, redirect to invite page or home
      window.location.href = redirectUrl;
    } catch (err) {
      if (err instanceof EmailAuthError) {
        if (err.code === 'EMAIL_NOT_VERIFIED') {
          // Redirect to verification page (preserve invite token)
          navigate({ to: '/verify-email', search: { email, invite: inviteToken } });
          return;
        }
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col items-center justify-center p-6">
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

      <Card variant="bordered" className="relative max-w-md w-full shadow-2xl">
        <CardContent className="p-8 space-y-8">
          {/* Logo/Brand Section */}
          <div className="text-center space-y-3">
            <div className="flex justify-center mb-2">
              <CofactorMark size={64} className="text-foreground" />
            </div>
            <Heading level={1} className="text-2xl font-bold text-foreground">
              {name}
            </Heading>
            <Text className="text-muted-foreground">{tagline}</Text>
          </div>

          <Divider />

          {/* Sign In Section */}
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Text size="sm" className="text-muted-foreground">
                {hostOrganizationName
                  ? `Sign in to ${hostOrganizationName}`
                  : 'Sign in to continue to your testing workspace'}
              </Text>
            </div>

            {/* SSO Buttons */}
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              iconLeft={<GoogleIcon />}
              onPress={() => {
                window.location.href = ssoUrl('google');
              }}
            >
              Sign in with Google
            </Button>

            {authConfig?.providers.microsoft.enabled && (
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                iconLeft={<MicrosoftIcon />}
                onPress={() => {
                  window.location.href = ssoUrl('microsoft');
                }}
              >
                Sign in with Microsoft
              </Button>
            )}

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-muted" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            {/* Email/Password Toggle */}
            {!showEmailForm ? (
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onPress={() => setShowEmailForm(true)}
              >
                Sign in with email
              </Button>
            ) : (
              <form onSubmit={handleEmailSignin} className="space-y-4">
                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                    <Text size="sm" className="text-destructive">
                      {error}
                    </Text>
                  </div>
                )}

                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  autoComplete="email"
                  isRequired
                />

                <PasswordField
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  autoComplete="current-password"
                  isRequired
                />

                <div className="text-right">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isDisabled={signinMutation.isPending}
                >
                  {signinMutation.isPending ? (
                    <Flex align="center" gap="sm">
                      <Spinner size="sm" />
                      <span>Signing in...</span>
                    </Flex>
                  ) : (
                    'Sign in'
                  )}
                </Button>

                <div className="text-center">
                  <Text size="sm" className="text-muted-foreground">
                    Don't have an account?{' '}
                    <Link
                      to="/signup"
                      search={{ invite: inviteToken, returnTo: returnTo ?? undefined }}
                      className="text-primary hover:underline"
                    >
                      Sign up
                    </Link>
                  </Text>
                </div>
              </form>
            )}

            <Text size="xs" className="text-center text-muted-foreground">
              By signing in, you agree to our{' '}
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground transition-colors"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground transition-colors"
              >
                Privacy Policy
              </a>
            </Text>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-6 text-center">
        <Text size="xs" className="text-muted-foreground">
          © {new Date().getFullYear()} {name}. All rights reserved.
        </Text>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
